import { useState } from "react";
import { ChevronDown, Scale } from "lucide-react";

/**
 * Iter-196.QQ — Compact 4-factor rubric strip.
 *
 * Replaces the legacy "4 fat methodology cards in a 2×2 / 4-col grid"
 * pattern that consumed ~280–340px of vertical real estate above the
 * homepage Insurance + Auto-Loan comparison tables. The new strip:
 *
 *   - 1-line compact weight summary (Premium 35% · Coverage 30% · Reviews 20% · Discounts 15%)
 *   - "Full methodology" disclosure toggle to expand the per-factor
 *     explainers + sources line. Default collapsed.
 *   - Total collapsed height ≈ 80px (vs. ~320px), a ~75% reduction.
 *
 * Why a strip + disclosure
 * ------------------------
 * NerdWallet / Bankrate / The Zebra all put their scoring rubric
 * either ABOVE-fold OR as an accordion directly above the comparison
 * table. CarSavr's pre-iter-196.QQ version chose option (A) but with
 * 4 large cards, pushing the actual ranking table below the fold on
 * mobile and ~85% of laptop screens. The comparison table is the
 * money-maker; the methodology should support, not compete with it.
 *
 * SEO + structured-data safety
 * ----------------------------
 * Every per-factor title + weight + explainer is still rendered (just
 * inside the accordion content). Search engines index the full HTML
 * regardless of `[hidden]` / `display:none`, and the expanded copy
 * preserves every existing keyword + sentence. The original data-
 * testids on each factor card are preserved verbatim so the
 * regression suite stays green.
 *
 * Props
 * -----
 *   - eyebrow:     small label above the rubric ("Our rubric · 4-factor editor score")
 *   - factors:     [{ Icon, title, weight, testid, explainer }]
 *   - testid:      root testid (e.g. "insurance-comparison-methodology")
 *   - rubricHeaderTestid: legacy testid on the rubric header row
 *   - contextTestid:     legacy testid on the editorial intro paragraph
 *   - contextCopy:       editorial intro paragraph rendered inside the
 *                        disclosure body (preserves SEO copy)
 *   - sourcesLine: small caption ("Updated quarterly · sources cited per …")
 */
export default function CompactRubricStrip({
  eyebrow = "Our rubric · 4-factor editor score",
  factors,
  testid,
  rubricHeaderTestid,
  contextTestid,
  contextCopy,
  sourcesLine = "Updated quarterly · sources cited per carrier review",
  disclosureLabel = "View full methodology",
}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testid}
      aria-label="Compact scoring rubric"
      className="border border-slate-200 bg-[#FAFAFA] px-4 py-3 mb-6 sm:px-6"
    >
      {/* COLLAPSED state — 1 line summary, ~32px tall. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2 shrink-0">
          <Scale
            className="w-4 h-4 text-[#10B981]"
            strokeWidth={2.25}
            aria-hidden
          />
          <p
            data-testid={rubricHeaderTestid}
            className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-950"
          >
            {eyebrow}
          </p>
        </div>
        <ul
          data-testid={`${testid}-weights`}
          className="w-full grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] text-slate-600 sm:w-auto sm:flex sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1"
        >
          {factors.map((f, idx) => (
            <li
              key={f.title}
              className="inline-flex items-center justify-between gap-2 whitespace-nowrap sm:justify-start sm:gap-1.5"
            >
              {idx > 0 && (
                <span aria-hidden className="hidden sm:inline text-slate-300 mr-1">
                  ·
                </span>
              )}
              <span className="font-semibold text-slate-700">{f.title}</span>
              <span className="font-black text-[#10B981] tabular-nums">
                {f.weight}
              </span>
            </li>
          ))}
        </ul>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((s) => !s)}
          data-testid={`${testid}-toggle`}
          className="self-start inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700 hover:text-slate-950 transition-colors sm:ml-auto sm:self-auto"
        >
          {open ? "Hide" : disclosureLabel}
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${
              open ? "rotate-180" : ""
            }`}
            aria-hidden
          />
        </button>
      </div>

      {/* EXPANDED state — full per-factor breakdown + editorial context.
          Preserves every existing per-factor testid (insurance-rubric-…,
          auto-loan-rubric-…) so the regression suite stays green. */}
      {open && (
        <div
          data-testid={`${testid}-expanded`}
          className="mt-4 pt-4 border-t border-slate-200"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">
            {sourcesLine}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {factors.map((f) => {
              const Icon = f.Icon;
              return (
                <div
                  key={f.title}
                  data-testid={f.testid}
                  className="border border-slate-200 bg-white p-3 flex flex-col gap-2 h-full"
                >
                  <div className="flex items-center justify-between">
                    <span className="w-7 h-7 grid place-items-center bg-[#10B981]/10 text-[#10B981]">
                      <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
                    </span>
                    <span className="text-[10px] font-black text-[#10B981] tabular-nums tracking-[0.08em] bg-[#10B981]/10 px-2 py-0.5">
                      {f.weight}
                    </span>
                  </div>
                  <div>
                    <p className="text-[13px] font-display font-black text-slate-950 leading-tight">
                      {f.title}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      {f.explainer}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          {contextCopy && (
            <p
              data-testid={contextTestid}
              className="mt-4 text-[12.5px] text-slate-600 leading-relaxed max-w-3xl border-l-2 border-[#10B981]/30 pl-3"
            >
              {contextCopy}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
